import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { PageHeader, Spinner } from '@/components/Common';
import { StatusPill, V3 } from '@/components/v3/kit';
import { ExemptionBanner } from '@/components/Threshold';
import { TEST_IDS } from '@/constants/ids';
import SetupTab from '@/pages/engagement/SetupTab';
import FindingsTab from '@/pages/engagement/FindingsTab';
import ChecklistTab from '@/pages/engagement/ChecklistTab';
import DocReviewTab from '@/pages/engagement/DocReviewTab';
import DocCoverageTab from '@/pages/engagement/DocCoverageTab';
import MediaTab from '@/pages/engagement/MediaTab';
import ReportTab from '@/pages/engagement/ReportTab';
import HistoryTab from '@/pages/engagement/HistoryTab';
import { CaretLeftIcon, RepeatIcon, LinkSimpleIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

// GL-SPEC-APP-008 — Checklist and Doc Review tabs consolidated into the
// Docs tab. Legacy tabs are hidden from default navigation but remain
// reachable via the ?legacy=1 query string for 30 days so historical
// engagements can be referenced if a deliverable raises questions about
// the underlying source data.
const TABS_BY_TYPE_DEFAULT = {
  walkthrough: ['setup', 'findings', 'coverage', 'media', 'report', 'history'],
  gap_check: ['setup', 'coverage', 'media', 'report', 'history'],
  combined: ['setup', 'findings', 'coverage', 'media', 'report', 'history'],
  incident_investigation: ['setup', 'findings', 'coverage', 'media', 'report', 'history'],
};

// Legacy view — adds Checklist + Doc Review back in for read-only review.
const TABS_BY_TYPE_LEGACY = {
  walkthrough: ['setup', 'findings', 'coverage', 'media', 'report', 'history'],
  gap_check: ['setup', 'coverage', 'checklist', 'docs', 'media', 'report', 'history'],
  combined: ['setup', 'findings', 'coverage', 'checklist', 'docs', 'media', 'report', 'history'],
  incident_investigation: ['setup', 'findings', 'coverage', 'media', 'report', 'history'],
};

// GL-DESIGN-V3 — header chips rendered with StatusPill (tint bg + colored dot)
const TYPE_LABEL = {
  walkthrough: 'Walkthrough',
  gap_check: 'Doc review',
  combined: 'Combined',
  incident_investigation: 'Incident',
};
const STATUS_META = {
  in_progress: { label: 'In progress', dot: 'var(--v3-mid-navy)' },
  report_ready: { label: 'Dossier ready', dot: 'var(--v3-gold)' },
  delivered: { label: 'Delivered', dot: 'var(--v3-p3)' },
  archived: { label: 'Archived', dot: 'var(--v3-muted)' },
};

const TAB_LABEL = {
  setup: 'Setup',
  findings: 'Findings',
  coverage: 'Docs',
  checklist: 'Checklist (legacy)',
  docs: 'Doc Review (legacy)',
  media: 'Media',
  report: 'Dossier',
  history: 'History',
};
const TAB_TESTID = {
  setup: TEST_IDS.tabSetup,
  findings: TEST_IDS.tabFindings,
  coverage: 'tab-coverage',
  checklist: TEST_IDS.tabChecklist,
  docs: 'tab-docs',
  media: TEST_IDS.tabMedia,
  report: TEST_IDS.tabReport,
  history: 'tab-history',
};

export default function EngagementDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [eng, setEng] = useState(null);
  const [tab, setTab] = useState('setup');
  const [reloadKey, setReloadKey] = useState(0);
  // GL-SPEC-APP-036 — score trend for repeat-visit clients (small pill in header)
  const [scoreTrend, setScoreTrend] = useState(null);
  // GL-SPEC-APP-019 — cross-tab deep-link target. Set when a finding's
  // "View source doc" link is clicked; DocCoverageTab consumes and clears it.
  const [focusDocKey, setFocusDocKey] = useState(null);
  // Reverse direction — Docs tab -> Findings tab jump
  const [focusFindingId, setFocusFindingId] = useState(null);

  function jumpToDoc(docKey) {
    setFocusDocKey(docKey);
    setTab('coverage');
  }

  function jumpToFinding(findingId) {
    setFocusFindingId(findingId);
    setTab('findings');
  }

  useEffect(() => {
    let mounted = true;
    api
      .get(`/engagements/${id}`)
      .then((r) => mounted && setEng(r.data))
      .catch((e) => {
        toast.error(formatApiError(e));
        nav('/');
      });
    return () => {
      mounted = false;
    };
  }, [id, reloadKey, nav]);

  // GL-SPEC-APP-036 — Fetch score-trend once we have the engagement. Non-blocking.
  useEffect(() => {
    if (!eng?.id) return;
    let mounted = true;
    api
      .get(`/engagements/${eng.id}/score_trend`)
      .then((r) => mounted && setScoreTrend(r.data))
      .catch(() => {
        // silent — trend is a nice-to-have, don't toast on failure
      });
    return () => {
      mounted = false;
    };
  }, [eng?.id, reloadKey]);

  function refresh() {
    setReloadKey((k) => k + 1);
  }

  async function createFollowUp() {
    if (!window.confirm(`Create a follow-up visit for ${eng.business_name}?\n\nThis copies the facility info, carries over unresolved P1/P2 findings, and pre-fills any P1/P2 gap items that were Missing or Incomplete last visit.`)) return;
    try {
      const { data } = await api.post(`/engagements/${eng.id}/follow_up`);
      toast.success('Follow-up engagement created');
      nav(`/engagements/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const [searchParams] = useSearchParams();
  const isLegacyView = searchParams.get('legacy') === '1';
  const initialTab = searchParams.get('tab'); // GL-SPEC-APP-039 — deep-link target tab
  const TABS_BY_TYPE = isLegacyView ? TABS_BY_TYPE_LEGACY : TABS_BY_TYPE_DEFAULT;

  const tabs = eng ? TABS_BY_TYPE[eng.type] || ['setup'] : ['setup'];

  useEffect(() => {
    if (!eng) return;
    // Honor ?tab=findings|coverage|report on first load if it's a valid tab
    // for this engagement type; otherwise fall back to the first tab.
    if (initialTab && tabs.includes(initialTab)) {
      setTab(initialTab);
    } else if (!tabs.includes(tab)) {
      setTab(tabs[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eng?.type]);

  if (!eng) {
    return (
      <div className="p-8">
        <Spinner label="Loading engagement…" />
      </div>
    );
  }

  return (
    <>
      <div className="px-5 md:px-8 pt-5">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
          style={{ color: 'var(--gl-navy)', fontFamily: 'Inter', letterSpacing: '0.1em' }}
          data-testid="back-to-dashboard"
        >
          <CaretLeftIcon size={16} weight="bold" /> Back to Dashboard
        </Link>
      </div>
      <PageHeader
        eyebrow={`${TAB_LABEL[tab]} · Engagement`}
        title={eng.business_name}
        subtitle={[eng.site_address, eng.site_address_note, eng.industry].filter(Boolean).join(' · ')}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span data-testid={`type-badge-${eng.type}`}>
              <StatusPill dot={V3.navy} bg={V3.tint}>{TYPE_LABEL[eng.type] || 'Combined'}</StatusPill>
            </span>
            <span data-testid={`status-chip-${eng.status}`}>
              <StatusPill dot={STATUS_META[eng.status]?.dot || V3.navy}>{STATUS_META[eng.status]?.label || 'In progress'}</StatusPill>
            </span>
            {scoreTrend && scoreTrend.delta !== null && scoreTrend.direction && (
              <span
                data-testid="score-trend-pill"
                title={
                  scoreTrend.direction === 'flat'
                    ? `Compliance score unchanged from prior visit (${scoreTrend.prior_visit_date || 'earlier'})`
                    : `Compliance score ${scoreTrend.direction === 'up' ? 'improved' : 'declined'} vs prior visit (${scoreTrend.prior_visit_date || 'earlier'})`
                }
              >
                <StatusPill dot={scoreTrend.direction === 'up' ? V3.p3 : scoreTrend.direction === 'down' ? V3.p1 : V3.muted}>
                  <span style={{ fontFamily: V3.mono }}>
                    {scoreTrend.direction === 'up' ? '↑' : scoreTrend.direction === 'down' ? '↓' : '→'} {scoreTrend.delta > 0 ? '+' : ''}{scoreTrend.delta}
                  </span>
                  &nbsp;vs prior
                </StatusPill>
              </span>
            )}
            {(eng.status === 'delivered' || eng.status === 'archived') && (
              <button
                className="gl-btn gl-btn-accent !h-10 !px-4 !text-xs"
                onClick={createFollowUp}
                data-testid="create-followup-btn"
                title="Create a follow-up visit — carries unresolved P1/P2 findings + gaps"
              >
                <RepeatIcon size={16} weight="bold" /> Follow-Up Visit
              </button>
            )}
          </div>
        }
      />

      {eng.parent_engagement_id && (
        <div
          className="mx-5 md:mx-8 mb-2 p-3 border-l-4 text-sm flex items-center gap-2 flex-wrap"
          style={{ borderColor: 'var(--gl-gold)', background: '#fdf6e3', color: 'var(--gl-dark)' }}
          data-testid="parent-banner"
        >
          <LinkSimpleIcon size={18} weight="bold" />
          <span>
            <b>Follow-up visit.</b> This engagement carries over unresolved P1/P2 findings and gap items from a prior engagement.
          </span>
          <Link
            to={`/engagements/${eng.parent_engagement_id}`}
            className="ml-auto gl-btn gl-btn-secondary !h-9 !px-3 !text-xs"
            data-testid="parent-link"
          >
            Open Prior Visit →
          </Link>
        </div>
      )}

      <ExemptionBanner exemptions={eng.exemptions} />

      {eng.status === 'archived' && (
        <div
          className="mx-5 md:mx-8 mb-2 p-3 border-l-4 text-sm flex items-start gap-2"
          style={{ borderColor: '#374151', background: '#f3f4f6', color: '#1f2937' }}
          data-testid="archived-banner"
        >
          <div>
            <b>This engagement is archived.</b> All data is preserved and the client share link is still active. Use <i>Restore to Delivered</i> in the Setup tab to bring it back to your active dashboard.
          </div>
        </div>
      )}

      <div className="border-b-2 border-gray-200 flex gap-1 px-5 md:px-8 overflow-x-auto hide-scrollbar sticky top-0 bg-[var(--gl-bg)] z-10">
        {tabs.map((t) => (
          <button
            key={t}
            className={`gl-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
            data-testid={TAB_TESTID[t]}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="px-5 md:px-8 py-6">
        {tab === 'setup' && <SetupTab eng={eng} refresh={refresh} />}
        {tab === 'findings' && (
          <FindingsTab
            eng={eng}
            onJumpToDoc={jumpToDoc}
            focusFindingId={focusFindingId}
            onFocusConsumed={() => setFocusFindingId(null)}
          />
        )}
        {tab === 'coverage' && (
          <DocCoverageTab
            eng={eng}
            focusDocKey={focusDocKey}
            onFocusConsumed={() => setFocusDocKey(null)}
            onJumpToFinding={jumpToFinding}
          />
        )}
        {tab === 'checklist' && <ChecklistTab eng={eng} />}
        {tab === 'docs' && <DocReviewTab eng={eng} />}
        {tab === 'media' && <MediaTab eng={eng} />}
        {tab === 'report' && <ReportTab eng={eng} refresh={refresh} onJumpTab={setTab} />}
        {tab === 'history' && <HistoryTab engagement={eng} />}
      </div>
    </>
  );
}
